import React, { useState, useCallback, useEffect, useMemo, lazy, Suspense } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Chip, CircularProgress, Tabs, Tab,
  Snackbar, Alert, Button, Card, Skeleton,
} from '@mui/material';
import { ArrowBack, CallMerge, AutoFixHigh, FileDownload, WarningAmber } from '@mui/icons-material';
import { ScanProvider, useScan } from './ScanContext';
import { scoreColor, scoreGrade } from './utils';
import { useLazyGetScanSbomQuery, useUpdateOnboardingStateMutation } from '../../store/api/githubApi';
import AgentProgressDrawer from '../../components/refactor/AgentProgressDrawer';

// Eager load default landing page
import OverviewPage from './pages/OverviewPage';

// Lazy-load heavy sections so they don't block the main thread or load bundle code unnecessarily
const IssuesPage = lazy(() => import('./pages/IssuesPage'));
const FilesPage = lazy(() => import('./pages/FilesPage'));
const SavingsPage = lazy(() => import('./pages/SavingsPage'));
const CapacityPage = lazy(() => import('./pages/CapacityPage'));
const FixesPage = lazy(() => import('./pages/FixesPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const RefactorPage = lazy(() => import('./pages/RefactorPage'));
const SupplyChainPage = lazy(() => import('./pages/SupplyChainPage'));

// ── Tab configuration & Types ──────────────────────────────────────────────────

export type ScanTabKey =
  | 'overview'
  | 'issues'
  | 'files'
  | 'savings'
  | 'capacity'
  | 'supply-chain'
  | 'fixes'
  | 'history'
  | 'refactor';

interface TabConfig {
  key: ScanTabKey;
  pathSuffix: string;
  title: string;
  subtitle: string;
  getLabel: (issuesCount: number, fixableCount: number, prsCount: number) => string;
  Component: React.ComponentType;
}

export const TAB_CONFIGS: TabConfig[] = [
  {
    key: 'overview',
    pathSuffix: '',
    title: 'Overview',
    subtitle: 'Preparing codebase health summary',
    getLabel: () => 'Overview',
    Component: OverviewPage,
  },
  {
    key: 'issues',
    pathSuffix: 'issues',
    title: 'Actions',
    subtitle: 'Preparing findings',
    getLabel: (issuesCount) => `Actions${issuesCount ? ` (${issuesCount})` : ''}`,
    Component: IssuesPage,
  },
  {
    key: 'files',
    pathSuffix: 'files',
    title: 'Code findings',
    subtitle: 'Grouping findings by file',
    getLabel: () => 'Code findings',
    Component: FilesPage,
  },
  {
    key: 'savings',
    pathSuffix: 'savings',
    title: 'Performance',
    subtitle: 'Preparing compute & memory optimizations',
    getLabel: () => 'Performance',
    Component: SavingsPage,
  },
  {
    key: 'capacity',
    pathSuffix: 'capacity',
    title: 'Capacity',
    subtitle: 'Preparing load capacity analysis',
    getLabel: () => 'Capacity',
    Component: CapacityPage,
  },
  {
    key: 'supply-chain',
    pathSuffix: 'supply-chain',
    title: 'Supply Chain',
    subtitle: 'Mapping your dependency graph for the weakest link',
    getLabel: () => 'Supply Chain',
    Component: SupplyChainPage,
  },
  {
    key: 'fixes',
    pathSuffix: 'fixes',
    title: 'Fixes & PRs',
    subtitle: 'Checking pull requests and remediation status',
    getLabel: (_, fixableCount, prsCount) =>
      `Fixes & PRs${fixableCount > 0 ? ` (${fixableCount} ready)` : prsCount > 0 ? ` (${prsCount} PRs)` : ''}`,
    Component: FixesPage,
  },
  {
    key: 'history',
    pathSuffix: 'history',
    title: 'History',
    subtitle: 'Loading scan trends and history',
    getLabel: () => 'History',
    Component: HistoryPage,
  },
  {
    key: 'refactor',
    pathSuffix: 'refactor',
    title: 'Refactor',
    subtitle: 'Preparing automated refactoring plans',
    getLabel: () => 'Refactor',
    Component: RefactorPage,
  },
];

export function getTabKeyFromPath(pathname: string, scanId: string): ScanTabKey {
  const base = `/scans/${scanId}`;
  if (!pathname.startsWith(base)) return 'overview';
  const relative = pathname.slice(base.length);
  const clean = relative.replace(/^\/+/, '').split('/')[0].split('?')[0];

  switch (clean) {
    case 'issues':   return 'issues';
    case 'files':    return 'files';
    case 'savings':  return 'savings';
    case 'capacity': return 'capacity';
    case 'supply-chain': return 'supply-chain';
    case 'fixes':    return 'fixes';
    case 'history':  return 'history';
    case 'refactor': return 'refactor';
    default:         return 'overview';
  }
}

// ── Section Skeleton Loader ───────────────────────────────────────────────────

export const SectionSkeletonLoader: React.FC<{
  title: string;
  subtitle: string;
}> = ({ title, subtitle }) => {
  return (
    <Box sx={{ width: '100%', py: 2 }}>
      {/* Title & subtle activity indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, px: 0.5 }}>
        <CircularProgress size={18} thickness={4} sx={{ color: 'primary.main' }} />
        <Typography variant="body2" fontWeight={700} color="text.primary">
          Loading {title}…
        </Typography>
        <Typography variant="caption" color="text.secondary">
          — {subtitle}
        </Typography>
      </Box>

      {/* Header card skeleton */}
      <Box sx={{
        p: 2.5, mb: 2.5, bgcolor: '#ffffff', borderRadius: 2.5,
        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <Skeleton variant="text" width="28%" height={28} sx={{ borderRadius: 1 }} />
        <Skeleton variant="text" width="55%" height={18} sx={{ mt: 0.5, borderRadius: 1 }} />
      </Box>

      {/* Finding card skeletons */}
      {[1, 2, 3].map((idx) => (
        <Box
          key={idx}
          sx={{
            p: 2,
            mb: 1.5,
            bgcolor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Skeleton variant="rectangular" width={68} height={24} sx={{ borderRadius: 1.5 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
          <Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: 1.5 }} />
        </Box>
      ))}
    </Box>
  );
};

// ── Section Error Boundary ────────────────────────────────────────────────────

interface SectionErrorBoundaryProps {
  sectionTitle: string;
  onRetry?: () => void;
  children: React.ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error in section ${this.props.sectionTitle}:`, error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card
          elevation={0}
          sx={{
            p: 4,
            my: 2.5,
            border: '1px solid',
            borderColor: 'error.200',
            borderRadius: 2.5,
            bgcolor: '#fff5f5',
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 2 }}>
            <WarningAmber sx={{ color: 'error.main', fontSize: 40 }} />
            <Typography variant="h6" fontWeight={800} color="error.900">
              Unable to load {this.props.sectionTitle}
            </Typography>
            <Typography variant="body2" color="error.800" sx={{ maxWidth: 460, mb: 1, lineHeight: 1.6 }}>
              Something went wrong while loading this section.
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={this.handleRetry}
              sx={{ textTransform: 'none', fontWeight: 700, px: 3, py: 0.75, borderRadius: 1.5 }}
            >
              Retry
            </Button>
          </Box>
        </Card>
      );
    }
    return this.props.children;
  }
}

// ── Sub-nav with Immediate Active State ────────────────────────────────────────

const ScanSubNav: React.FC<{
  activeTabKey: ScanTabKey;
}> = ({ activeTabKey }) => {
  const { scanId, issues, prs } = useScan();
  const base = `/scans/${scanId}`;

  const fixableCount = useMemo(() => {
    return issues.filter(
      (i) => ['trivial', 'simple', 'moderate'].includes(i.ai_fix_complexity ?? '') && i.status !== 'pr_raised'
    ).length;
  }, [issues]);

  const tabs = useMemo(() => {
    return TAB_CONFIGS.map((cfg) => ({
      key: cfg.key,
      path: cfg.pathSuffix ? `${base}/${cfg.pathSuffix}` : base,
      label: cfg.getLabel(issues.length, fixableCount, prs.length),
    }));
  }, [base, issues.length, fixableCount, prs.length]);

  const currentTabIdx = tabs.findIndex((t) => t.key === activeTabKey);

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
      <Tabs
        value={currentTabIdx === -1 ? 0 : currentTabIdx}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          '& .MuiTabs-indicator': {
            transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
        TabIndicatorProps={{ style: { height: 2 } }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            label={t.label}
            component={Link}
            to={t.path}
            sx={{ minHeight: 40, py: 0.5, fontSize: 13, textTransform: 'none', fontWeight: 500 }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

// ── Scan Tab Content Host (Preserves Loaded Tabs + Section Loader) ─────────────

const ScanTabHost: React.FC<{
  activeTabKey: ScanTabKey;
}> = ({ activeTabKey }) => {
  // Track all tabs that have been initialized/mounted so far (preserves state on return)
  const [loadedTabs, setLoadedTabs] = useState<Set<ScanTabKey>>(() => new Set([activeTabKey]));
  // If destination tab has never been mounted before, show the lightweight loading state first
  const [preparingTab, setPreparingTab] = useState<ScanTabKey | null>(null);
  const [retryKey, setRetryKey] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!loadedTabs.has(activeTabKey)) {
      setPreparingTab(activeTabKey);
      // Yield to the browser frame so the active tab blue underline
      // and lightweight loading state paint on screen FIRST (0ms delay)
      const timer = setTimeout(() => {
        setLoadedTabs((prev) => {
          const next = new Set(prev);
          next.add(activeTabKey);
          return next;
        });
        setPreparingTab(null);
      }, 16);
      return () => clearTimeout(timer);
    } else {
      setPreparingTab(null);
    }
  }, [activeTabKey, loadedTabs]);

  const activeConfig = TAB_CONFIGS.find((c) => c.key === activeTabKey) || TAB_CONFIGS[0];

  return (
    <Box sx={{ position: 'relative', minHeight: 300 }}>
      {preparingTab === activeTabKey && (
        <SectionSkeletonLoader
          title={activeConfig.title}
          subtitle={activeConfig.subtitle}
        />
      )}

      {TAB_CONFIGS.map((cfg) => {
        const isLoaded = loadedTabs.has(cfg.key);
        if (!isLoaded) return null;

        const isCurrentlyActive = activeTabKey === cfg.key && preparingTab !== cfg.key;
        const Component = cfg.Component;
        const currentRetryCount = retryKey[cfg.key] ?? 0;

        return (
          <Box
            key={`${cfg.key}-${currentRetryCount}`}
            role="tabpanel"
            hidden={!isCurrentlyActive}
            sx={{
              display: isCurrentlyActive ? 'block' : 'none',
            }}
          >
            <SectionErrorBoundary
              sectionTitle={cfg.title}
              onRetry={() => {
                setRetryKey((prev) => ({ ...prev, [cfg.key]: (prev[cfg.key] ?? 0) + 1 }));
              }}
            >
              <Suspense
                fallback={
                  <SectionSkeletonLoader
                    title={cfg.title}
                    subtitle={cfg.subtitle}
                  />
                }
              >
                <Component />
              </Suspense>
            </SectionErrorBoundary>
          </Box>
        );
      })}
    </Box>
  );
};

// ── Header strip ──────────────────────────────────────────────────────────────

const ScanHeader: React.FC = () => {
  const { scan, isRunning, prs, scanId, showSnackbar } = useScan();
  const navigate = useNavigate();
  const grade = scoreGrade(scan?.debt_score ?? null);
  const color = scoreColor(scan?.debt_score ?? null);

  const [fetchSbom, { isFetching: isDownloadingSbom }] = useLazyGetScanSbomQuery();

  const handleDownloadSbom = async () => {
    try {
      const sbom = await fetchSbom(Number(scanId)).unwrap();
      const blob = new Blob([JSON.stringify(sbom, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kodegauge-sbom-scan-${scanId}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Failed to generate SBOM. Please try again.');
    }
  };

  const gradeColors: Record<string, { bg: string; color: string }> = {
    success: { bg: '#ecfdf5', color: '#059669' },
    warning: { bg: '#fffbeb', color: '#d97706' },
    error:   { bg: '#fff5f5', color: '#dc2626' },
  };
  const gc = gradeColors[color] ?? gradeColors.error;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
      flexWrap: 'wrap',
    }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        size="small"
        sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
      >
        Back
      </Button>

      <Box sx={{ width: '1px', height: 20, bgcolor: 'divider' }} />

      {scan && (
        <>
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: 280 }}>
            Scan #{scanId}
          </Typography>
          <Chip
            label={scan.branch}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, height: 20, borderRadius: 1 }}
          />
          {scan.commit_sha && (
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
              {scan.commit_sha.slice(0, 7)}
            </Typography>
          )}
          {scan.loc_total != null && (
            <Typography variant="caption" color="text.disabled">
              {scan.loc_total.toLocaleString()} LOC
            </Typography>
          )}
        </>
      )}

      <Box sx={{ flex: 1 }} />

      {isRunning && (
        <Chip
          icon={<CircularProgress size={11} />}
          label={`Scanning… ${scan?.status ?? ''}`}
          color="info"
          variant="outlined"
          size="small"
        />
      )}

      {prs.length > 0 && (
        <Chip
          icon={<CallMerge sx={{ fontSize: '14px !important' }} />}
          label={`${prs.length} PR${prs.length > 1 ? 's' : ''} raised`}
          color="success"
          size="small"
        />
      )}

      {scan && (
        <Button
          size="small"
          variant="outlined"
          startIcon={isDownloadingSbom ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: '16px !important' }} />}
          onClick={handleDownloadSbom}
          disabled={isDownloadingSbom}
          sx={{ textTransform: 'none', fontSize: 12.5 }}
        >
          SBOM
        </Button>
      )}

      {scan?.debt_score != null && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          px: 1.5, py: 0.5, borderRadius: 2,
          bgcolor: gc.bg, border: `1px solid ${gc.color}30`,
        }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Health score</Typography>
          <Typography variant="subtitle2" fontWeight={900} sx={{ color: gc.color }}>
            {scan.debt_score} · {grade}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Inner layout (uses context) ───────────────────────────────────────────────

const ScanLayoutInner: React.FC = () => {
  const { isLoading, error, fixingCount, refreshPolling, scanId, scan } = useScan();
  const location = useLocation();
  const [updateOnboarding] = useUpdateOnboardingStateMutation();

  const routeTabKey = useMemo(() => {
    return getTabKeyFromPath(location.pathname, scanId);
  }, [location.pathname, scanId]);
  const activeTabKey = routeTabKey;

  useEffect(() => {
    if (scan?.status === 'complete') {
      updateOnboarding({ completed_step: 'review_results', last_step_key: 'review_results' });
    }
  }, [scan?.status, updateOnboarding]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading scan results…</Typography>
      </Box>
    );
  }

  if (error) {
    const errorObj = error as any;
    const detail = errorObj?.data?.detail || (errorObj?.status === 404 ? 'The requested scan could not be found or may have been removed.' : 'A network or server error occurred while retrieving scan data.');
    return (
      <Card elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'error.200', borderRadius: 2, bgcolor: 'error.50', my: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <WarningAmber color="error" sx={{ fontSize: 24, mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800} color="error.900">
              ⚠ Scan results unavailable
            </Typography>
            <Typography variant="body2" color="error.800" sx={{ mt: 0.5, lineHeight: 1.5 }}>
              <strong>What happened:</strong> Could not load scan details.
            </Typography>
            <Typography variant="body2" color="error.800" sx={{ mt: 0.25, lineHeight: 1.5 }}>
              <strong>Why:</strong> {detail}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => window.location.reload()}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Try again
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => window.location.href = '/repositories'}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Back to Repositories
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Box>
      <AgentProgressDrawer onComplete={refreshPolling} />
      <ScanHeader />
      {fixingCount > 0 && (
        <Alert
          severity="info"
          icon={<CircularProgress size={16} color="inherit" />}
          sx={{ mb: 2, py: 0.75, alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHigh sx={{ fontSize: 16 }} />
            <span>
              AI is generating fixes for <strong>{fixingCount} issue{fixingCount > 1 ? 's' : ''}</strong> — a PR will be raised on GitHub shortly.
            </span>
          </Box>
        </Alert>
      )}
      <ScanSubNav activeTabKey={activeTabKey} />
      <ScanTabHost activeTabKey={activeTabKey} />
      {/* Fallback outlet for any external sub-routes */}
      <Outlet />
    </Box>
  );
};

// ── Public export ─────────────────────────────────────────────────────────────

export const ScanLayout: React.FC = () => {
  const [snackbar, setSnackbar] = useState<{ msg: string; action?: React.ReactNode } | null>(null);

  const handleSnackbar = useCallback((msg: string, action?: React.ReactNode) => {
    setSnackbar({ msg, action });
  }, []);

  return (
    <ScanProvider onSnackbar={handleSnackbar}>
      <ScanLayoutInner />
      <Snackbar
        open={!!snackbar}
        autoHideDuration={8000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar?.msg}
        action={snackbar?.action}
      />
    </ScanProvider>
  );
};
